import { useEffect } from 'react';
import { useAppSelector } from 'common/hooks/state';
import { Mixpanel } from 'common/utils/mixpanel';
import { selectCurrentUser, selectToken } from 'features/login/authSlice';
import { selectOnboardingVariant, selectVariantSource } from 'features/Onboarding/onboardingSlice';
import { selectGuestToken } from './guestSessionSlice';

/**
 * Keeps the analytics super properties in step with the store, from launch
 * (rehydrated session) onwards, and again after a sign-out reset.
 *
 * Signed out: guests track on Mixpanel's own device id, so guest_token is how
 * their events join to the backend guest session, and onboarding_variant /
 * variant_source come from the device's sticky assignment.
 *
 * Signed in: the variant comes from the account (onboarding_profile, copied
 * from the guest session at claim), not the device, so it survives the
 * identity split, follows the user to a second device and never stamps a
 * different person who later signs in here. The payload has no source, so
 * variant_source is cleared. A missing value unregisters (see registerSuper).
 */
const GuestAnalyticsSync = (): null => {
  const guestToken = useAppSelector(selectGuestToken);
  const deviceVariant = useAppSelector(selectOnboardingVariant);
  const deviceVariantSource = useAppSelector(selectVariantSource);
  const authUser = useAppSelector(selectCurrentUser);
  const sessionToken = useAppSelector(selectToken);
  const account = authUser?.user ?? authUser;
  const accountId = account?.id ?? null;
  const isSignedIn = Boolean(accountId || sessionToken?.token);
  const accountVariant = (authUser?.user?.onboarding_profile ?? authUser?.onboarding_profile)
    ?.onboarding_variant ?? null;

  useEffect(() => {
    Mixpanel.registerSuper(isSignedIn
      ? {
        guest_token: guestToken,
        onboarding_variant: accountVariant,
        variant_source: null,
      }
      : {
        guest_token: guestToken,
        onboarding_variant: deviceVariant,
        variant_source: deviceVariantSource,
      });
  }, [isSignedIn, accountId, guestToken, accountVariant, deviceVariant, deviceVariantSource]);

  return null;
};

export default GuestAnalyticsSync;
